/**
 * Weekly overtime aggregation — computed at payroll time, not stored on time_logs.
 */

import {
  DEFAULT_ORG_SETTINGS,
  getEffectiveOrgSetting,
} from "./organizationSettings";

export const DEFAULT_WEEKLY_OVERTIME = Object.freeze({
  enabled: true,
  threshold_hours: 40,
  multiplier: 1.5,
});

export function resolveWeeklyOvertimeSettings(orgSettings) {
  const source = orgSettings || DEFAULT_ORG_SETTINGS;
  const configured = getEffectiveOrgSetting(source, "payroll.weekly_overtime", {});

  const legacyThreshold = getEffectiveOrgSetting(
    source,
    "payroll.overtime.weekly_threshold_hours",
    undefined,
  );
  const legacyMultiplier = getEffectiveOrgSetting(
    source,
    "payroll.overtime.multiplier",
    undefined,
  );

  const enabled =
    configured.enabled !== undefined
      ? configured.enabled !== false
      : legacyThreshold !== undefined || legacyMultiplier !== undefined
        ? true
        : DEFAULT_WEEKLY_OVERTIME.enabled;

  const thresholdRaw =
    configured.threshold_hours ??
    legacyThreshold ??
    DEFAULT_WEEKLY_OVERTIME.threshold_hours;
  const multiplierRaw =
    configured.multiplier ??
    legacyMultiplier ??
    DEFAULT_WEEKLY_OVERTIME.multiplier;

  const threshold_hours = Math.max(0, Number(thresholdRaw) || 0);
  const multiplier = Math.max(1, Number(multiplierRaw) || 1);

  return {
    enabled,
    threshold_hours,
    multiplier,
  };
}

export function getEmployeePayrollKey(row) {
  return row?.supabase_user_id || row?.employee || "unknown";
}

export function splitWeeklyHours(totalHours, thresholdHours, enabled = true) {
  const total = Math.max(0, Number(totalHours) || 0);

  if (!enabled) {
    return {
      regularHours: total,
      overtimeHours: 0,
    };
  }

  const threshold = Math.max(0, Number(thresholdHours) || 0);
  const regularHours = Math.min(total, threshold);
  const overtimeHours = Math.max(0, total - threshold);

  return { regularHours, overtimeHours };
}

export function computeBlendedHourlyRate(rows = []) {
  const totalHours = (rows || []).reduce(
    (sum, row) => sum + Number(row?.total_hours || 0),
    0,
  );

  if (totalHours <= 0) {
    return 0;
  }

  const weightedPay = (rows || []).reduce(
    (sum, row) =>
      sum + Number(row?.total_hours || 0) * Number(row?.hourly_rate || 0),
    0,
  );

  return weightedPay / totalHours;
}

/**
 * Compute regular hours, overtime hours, and gross pay for one employee's rows.
 */
export function computeEmployeePayrollTotals(
  rows = [],
  overtimeSettings = DEFAULT_WEEKLY_OVERTIME,
) {
  const settings = overtimeSettings || DEFAULT_WEEKLY_OVERTIME;
  const totalHours = (rows || []).reduce(
    (sum, row) => sum + Number(row?.total_hours || 0),
    0,
  );
  const hourlyRate = computeBlendedHourlyRate(rows);
  const { regularHours, overtimeHours } = splitWeeklyHours(
    totalHours,
    settings.threshold_hours,
    settings.enabled,
  );

  const grossPay =
    regularHours * hourlyRate +
    overtimeHours * hourlyRate * settings.multiplier;

  return {
    totalHours,
    regularHours,
    overtimeHours,
    hourlyRate,
    grossPay,
  };
}

export function aggregatePayrollRowsByEmployee(rows = [], orgSettings) {
  const overtimeSettings = resolveWeeklyOvertimeSettings(orgSettings);
  const groups = new Map();

  (rows || []).forEach((row) => {
    const key = getEmployeePayrollKey(row);
    const existing = groups.get(key) || [];
    existing.push(row);
    groups.set(key, existing);
  });

  const aggregations = new Map();

  groups.forEach((employeeRows, key) => {
    aggregations.set(key, {
      ...computeEmployeePayrollTotals(employeeRows, overtimeSettings),
      rows: employeeRows,
    });
  });

  return aggregations;
}

export function computePayrollPeriodTotals(rows = [], orgSettings) {
  const aggregations = aggregatePayrollRowsByEmployee(rows, orgSettings);

  let totalHours = 0;
  let regularHours = 0;
  let overtimeHours = 0;
  let grossPay = 0;

  aggregations.forEach((aggregation) => {
    totalHours += aggregation.totalHours;
    regularHours += aggregation.regularHours;
    overtimeHours += aggregation.overtimeHours;
    grossPay += aggregation.grossPay;
  });

  return {
    employeeCount: aggregations.size,
    totalHours,
    regularHours,
    overtimeHours,
    grossPay,
    byEmployee: aggregations,
  };
}

/** Line-item base pay (hours × rate) — not overtime-adjusted. */
export function computeRowBaseGrossPay(row) {
  const hours = Number(row?.total_hours || 0);
  const rate = Number(row?.hourly_rate || 0);
  return hours * rate;
}
